import { SettingsIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Schema } from "effect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePermission } from "~/access/AccessProvider";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Switch } from "~/components/ui/switch";
import { useLocalStorage } from "~/hooks/useLocalStorage";

import { getOkrsNavItem } from "./okrsNav";
import { OkrsPageHeader } from "./OkrsPageHeader";
import {
  directoryPersonStatus,
  personById,
  personDisplayName,
  visibleDirectoryPeople,
} from "./okrsLookups";
import { okrsPeopleQueryOptions, syncOkrsDirectoryMutationOptions } from "./okrsReactQuery";
import { OkrsTable, type OkrsColumn } from "./OkrsTable";
import type { OkrsFilterSpec } from "./tableQuery";
import type { OkrPerson } from "./types";

const ORG_CHART_VISIBILITY_KEY = "operators:okrs:org-chart-visibility:v1";
const ORG_CHART_PAGE_SIZE = 25;
const DIRECTORY_STATUS_LABELS = {
  active: "Active",
  archived: "Archived",
  suspended: "Suspended",
} as const;

const OrgChartVisibilitySchema = Schema.Struct({
  showArchived: Schema.Boolean,
  showSuspended: Schema.Boolean,
});

const DEFAULT_ORG_CHART_VISIBILITY: typeof OrgChartVisibilitySchema.Type = {
  showArchived: false,
  showSuspended: false,
};

export function OrgChartPage() {
  const item = getOkrsNavItem("org-chart");
  const canManage = usePermission("tool.okrs.manage");
  const queryClient = useQueryClient();
  const peopleQuery = useQuery(okrsPeopleQueryOptions());
  const syncMutation = useMutation(syncOkrsDirectoryMutationOptions(queryClient));
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (peopleQuery.isError) {
    return <p className="text-sm text-destructive">Could not load the directory.</p>;
  }
  if (peopleQuery.isPending) {
    return <p className="text-sm text-muted-foreground">Loading directory…</p>;
  }

  return (
    <OrgChartPageView
      canManage={canManage}
      onResync={() => syncMutation.mutate()}
      onSettingsOpenChange={setSettingsOpen}
      people={peopleQuery.data.people}
      resyncBusy={syncMutation.isPending}
      resyncError={syncMutation.error instanceof Error ? syncMutation.error.message : null}
      settingsOpen={settingsOpen}
      syncedAt={peopleQuery.data.syncedAt}
      title={item?.label ?? "Org Chart"}
    />
  );
}

export function OrgChartPageView(props: {
  canManage: boolean;
  onResync?: () => void;
  onSettingsOpenChange?: (open: boolean) => void;
  people: readonly OkrPerson[];
  resyncBusy?: boolean;
  resyncError?: string | null;
  settingsOpen?: boolean;
  syncedAt: string | null;
  title: string;
}) {
  const peopleMap = personById(props.people);
  const [visibility, setVisibility] = useLocalStorage(
    ORG_CHART_VISIBILITY_KEY,
    DEFAULT_ORG_CHART_VISIBILITY,
    OrgChartVisibilitySchema,
  );
  const visiblePeople = useMemo(
    () => visibleDirectoryPeople(props.people, visibility),
    [props.people, visibility],
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      <OkrsPageHeader
        action={
          <Button
            aria-label="Directory settings"
            onClick={() => props.onSettingsOpenChange?.(true)}
            size="icon-sm"
            type="button"
            variant="outline"
          >
            <SettingsIcon />
          </Button>
        }
        description="People from the Google Workspace directory. Reporting lines come from Directory managers."
        title={props.title}
      />
      <OkrsTable
        columns={directoryColumns(peopleMap)}
        emptyMessage={
          props.people.length === 0 ? "No Workspace users yet" : "No people match these filters."
        }
        exportFilename="workspace-directory.csv"
        filters={directoryFilters()}
        pageSize={ORG_CHART_PAGE_SIZE}
        rowKey={(row) => row.id}
        rows={[...visiblePeople]}
        searchPlaceholder="Search name, email, title, or team"
        searchText={(row) =>
          `${row.displayName} ${row.email} ${row.title ?? ""} ${row.team ?? ""} ${personDisplayName(peopleMap, row.managerId)}`
        }
        toolbar={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={visibility.showArchived}
                onCheckedChange={(checked) =>
                  setVisibility((current) => ({ ...current, showArchived: Boolean(checked) }))
                }
              />
              Archived
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={visibility.showSuspended}
                onCheckedChange={(checked) =>
                  setVisibility((current) => ({ ...current, showSuspended: Boolean(checked) }))
                }
              />
              Suspended
            </label>
          </>
        }
      />
      <Dialog
        onOpenChange={(open) => props.onSettingsOpenChange?.(open)}
        open={props.settingsOpen ?? false}
      >
        <DialogPopup className="max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle>Directory settings</DialogTitle>
            <DialogDescription>
              Refresh the Google Workspace snapshot used by this table.
            </DialogDescription>
          </DialogHeader>
          <OrgChartSettingsPanel
            canManage={props.canManage}
            onResync={() => props.onResync?.()}
            resyncBusy={props.resyncBusy ?? false}
            resyncError={props.resyncError ?? null}
            syncedAt={props.syncedAt}
          />
        </DialogPopup>
      </Dialog>
    </div>
  );
}

export function OrgChartSettingsPanel(props: {
  canManage: boolean;
  onResync: () => void;
  resyncBusy: boolean;
  resyncError: string | null;
  syncedAt: string | null;
}) {
  return (
    <DialogPanel>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Workspace sync</p>
        <p className="text-sm text-muted-foreground">{formatSyncedAt(props.syncedAt)}</p>
        {props.canManage ? (
          <Button
            disabled={props.resyncBusy}
            onClick={props.onResync}
            size="sm"
            type="button"
            variant="outline"
          >
            {props.resyncBusy ? "Syncing…" : "Resync directory"}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Directory resync requires OKR manage permission.
          </p>
        )}
        {props.resyncError ? <p className="text-sm text-destructive">{props.resyncError}</p> : null}
      </div>
    </DialogPanel>
  );
}

function formatSyncedAt(syncedAt: string | null): string {
  if (!syncedAt) return "Not synced yet";
  const parsed = new Date(syncedAt);
  if (Number.isNaN(parsed.getTime())) return "Not synced yet";
  return `Last synced ${parsed.toLocaleString()}`;
}

function directoryColumns(peopleMap: Map<string, OkrPerson>): Array<OkrsColumn<OkrPerson>> {
  return [
    {
      key: "name",
      label: "Name",
      sortable: true,
      sortValue: (row) => row.displayName,
      csvValue: (row) => row.displayName,
      render: (row) => <span className="font-medium">{row.displayName}</span>,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      sortValue: (row) => row.email,
      csvValue: (row) => row.email,
      render: (row) => row.email,
    },
    {
      key: "title",
      label: "Title",
      sortable: true,
      sortValue: (row) => row.title ?? "",
      csvValue: (row) => row.title ?? "",
      render: (row) => row.title ?? "—",
    },
    {
      key: "team",
      label: "Team",
      sortable: true,
      sortValue: (row) => row.team ?? "",
      csvValue: (row) => row.team ?? "",
      render: (row) => row.team ?? "—",
    },
    {
      key: "manager",
      label: "Manager",
      sortable: true,
      sortValue: (row) => personDisplayName(peopleMap, row.managerId),
      csvValue: (row) => personDisplayName(peopleMap, row.managerId),
      render: (row) => personDisplayName(peopleMap, row.managerId),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortValue: (row) => directoryPersonStatus(row),
      csvValue: (row) => DIRECTORY_STATUS_LABELS[directoryPersonStatus(row)],
      render: (row) => {
        const status = directoryPersonStatus(row);
        return (
          <Badge size="sm" variant={status === "active" ? "success" : "secondary"}>
            {DIRECTORY_STATUS_LABELS[status]}
          </Badge>
        );
      },
    },
  ];
}

function directoryFilters(): Array<OkrsFilterSpec<OkrPerson>> {
  return [
    {
      id: "team",
      label: "Team",
      allLabel: "All teams",
      getValue: (row) => row.team ?? "",
    },
  ];
}
